import React from 'react'
import PropTypes from 'prop-types'
import './AlertCard.scss'

/**
 * Get icon for alert type
 */
const getIcon = (type) => {
	const icons = {
		info: (
			<svg viewBox="0 0 24 24" fill="currentColor" className="alert-card__icon-svg">
				<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
			</svg>
		),
		success: (
			<svg viewBox="0 0 24 24" fill="currentColor" className="alert-card__icon-svg">
				<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
			</svg>
		),
		warning: (
			<svg viewBox="0 0 24 24" fill="currentColor" className="alert-card__icon-svg">
				<path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
			</svg>
		),
		error: (
			<svg viewBox="0 0 24 24" fill="currentColor" className="alert-card__icon-svg">
				<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
			</svg>
		),
		danger: (
			<svg viewBox="0 0 24 24" fill="currentColor" className="alert-card__icon-svg">
				<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
			</svg>
		)
	}
	return icons[type] || icons.info
}

/**
 * Single alert item component
 */
const AlertItem = ({ item, type }) => {
	const itemType = item.type || type
	return (
		<div className={`alert-card alert-card--${itemType}`}>
			<div className="alert-card__icon">{getIcon(itemType)}</div>
			<div className="alert-card__content">
				{item.title && <h4 className="alert-card__title">{item.title}</h4>}
				{item.description && <p className="alert-card__message">{item.description}</p>}
				{item.message && !item.description && <p className="alert-card__message">{item.message}</p>}
				{item.timestamp && <span className="alert-card__timestamp">{item.timestamp}</span>}
			</div>
			<div className="alert-card__actions">
				{item.action && item.actionLabel && (
					<button className="alert-card__action-btn" onClick={item.action}>
						{item.actionLabel}
					</button>
				)}
			</div>
		</div>
	)
}

/**
 * AlertCard - Notification/alert display card
 * Supports both single alert and list of alerts
 *
 * @param {string} type - Alert type ('info' | 'success' | 'warning' | 'error' | 'danger')
 * @param {string} title - Alert title (for single alert mode)
 * @param {string} message - Alert message (for single alert mode)
 * @param {string} timestamp - When the alert occurred
 * @param {boolean} dismissible - Can be dismissed
 * @param {Function} onDismiss - Dismiss callback
 * @param {Function} onAction - Action button callback
 * @param {string} actionLabel - Action button text
 * @param {boolean} loading - Show loading state
 * @param {Array} items - List of alert items (for list mode)
 * @param {string} emptyMessage - Message to show when items array is empty
 */
const AlertCard = ({
	type = 'info',
	title,
	message,
	timestamp,
	dismissible = false,
	onDismiss,
	onAction,
	actionLabel,
	loading = false,
	items = null,
	emptyMessage = 'No items requiring attention'
}) => {
	// Loading state
	if (loading) {
		return (
			<div className={`alert-card alert-card--${type}`}>
				<div className="alert-card__skeleton">
					<div className="skeleton skeleton--icon"></div>
					<div className="skeleton skeleton--content">
						<div className="skeleton skeleton--title"></div>
						<div className="skeleton skeleton--message"></div>
					</div>
				</div>
			</div>
		)
	}

	// List mode - render multiple alerts
	if (items !== null) {
		if (!items || items.length === 0) {
			return (
				<div className="alert-card-list alert-card-list--empty">
					<div className="alert-card-list__empty">
						<svg viewBox="0 0 24 24" fill="currentColor" className="alert-card-list__empty-icon">
							<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
						</svg>
						<p className="alert-card-list__empty-text">{emptyMessage}</p>
					</div>
				</div>
			)
		}

		return (
			<div className="alert-card-list">
				{items.map((item, index) => (
					<AlertItem key={item.id || index} item={item} type={type} />
				))}
			</div>
		)
	}

	// Single alert mode
	return (
		<div className={`alert-card alert-card--${type}`}>
			<div className="alert-card__icon">{getIcon(type)}</div>
			<div className="alert-card__content">
				{title && <h4 className="alert-card__title">{title}</h4>}
				{message && <p className="alert-card__message">{message}</p>}
				{timestamp && <span className="alert-card__timestamp">{timestamp}</span>}
			</div>
			<div className="alert-card__actions">
				{onAction && actionLabel && (
					<button className="alert-card__action-btn" onClick={onAction}>
						{actionLabel}
					</button>
				)}
				{dismissible && onDismiss && (
					<button
						className="alert-card__dismiss-btn"
						onClick={onDismiss}
						aria-label="Dismiss alert"
					>
						<svg viewBox="0 0 24 24" fill="currentColor">
							<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
						</svg>
					</button>
				)}
			</div>
		</div>
	)
}

AlertCard.propTypes = {
	type: PropTypes.oneOf(['info', 'success', 'warning', 'error', 'danger']),
	title: PropTypes.string,
	message: PropTypes.string,
	timestamp: PropTypes.string,
	dismissible: PropTypes.bool,
	onDismiss: PropTypes.func,
	onAction: PropTypes.func,
	actionLabel: PropTypes.string,
	loading: PropTypes.bool,
	items: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		title: PropTypes.string,
		description: PropTypes.string,
		message: PropTypes.string,
		type: PropTypes.string,
		action: PropTypes.func,
		actionLabel: PropTypes.string,
		timestamp: PropTypes.string
	})),
	emptyMessage: PropTypes.string
}

export default AlertCard
